import React, { forwardRef, useMemo } from 'react';
import useLocale from '@/hooks/useLocale';
import * as Styles from './VPSInfo.styles';
import { useSelector } from 'react-redux';
import {
  IconGlobe, IconLocation2, IconChip, IconMemory, IconDisk,
} from 'gatorcomponents';

import { useIsMobile } from '@/hooks/useIsMobile';

import ContentSection from '@/pages/Products/VpsOciDetailPage/commons/ContentSection';

const VPSInfo = forwardRef((props, ref) => {
  const isMobile = useIsMobile();
  const vps = useSelector(state => state.vpsOci?.data?.vps);

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: locale } = productDetailsLocale;

  const sortedIps = useMemo(() => {
    const ipsArray = vps?.serverIps || [];
    return [...ipsArray].sort((a, b) => (b.main ? 1 : 0) - (a.main ? 1 : 0));
  }, [vps?.serverIps]);

  if (!vps || Object.keys(vps).length === 0) return null;

  const ipsData = sortedIps.length > 0 ? {
    icon: <IconGlobe
      backgroundColor="transparent"
      color="lowDark"
      secondaryColor="transparent"
      testId="IconGlobe"
      size="sm2"
    />,
    isMultipleIps: true,
    ips: sortedIps.map((ipItem, index) => {
      let label;
      if (sortedIps.length === 1) {
        label = locale.infoVps.serverIp;
      } else if (ipItem.main) {
        label = locale.infoVps.serverIpMain;
      } else {
        const ipNumber = sortedIps.slice(0, index).filter(ip => !ip.main).length + 2;
        label = `${locale.infoVps.serverIpSecondary} ${ipNumber}`;
      }
      return { label, value: ipItem.ip };
    }),
  } : null;

  const vpsData = [
    ipsData,
    {
      icon: <IconLocation2 testId="IconLocation2" size="sm2" />,
      label: locale.infoVps.datacenter,
      value: vps.datacenter,
    },
    {
      icon: <IconChip testId="IconChip" size="sm2" />,
      label: locale.infoVps.processor,
      value: vps.processor,
    },
    {
      icon: <IconMemory testId="IconMemory" size="sm2" />,
      label: locale.infoVps.memory,
      value: vps.memory,
    },
    {
      icon: <IconDisk testId="IconDisk" size="sm2" />,
      label: locale.infoVps.disk,
      value: vps.disk,
    },
  ].filter(item => item && (item.isMultipleIps || item.value));

  if (vpsData.length === 0) return null;

  return (
    <ContentSection
      ref={ref}
      direction="column"
      align="flex-start"
      justify="flex-start"
      flex={2}
      title={locale.infoVps.title.text}
      tooltip={locale.infoVps.title.tooltip}
      testId="vps-info-section"
    >
      <Styles.ListItems>
        {vpsData.map((item, index) => {
          if (item.isMultipleIps) {
            if (isMobile) {
              return (
                <Styles.Item $isMultipleIps key="vps-info-ips">
                  {item.ips.map((ip, idx) => (
                    <Styles.ItemLabelWrapper key={ip.value}>
                      <Styles.ItemLabel>
                        {idx === 0 && item.icon}
                        <Styles.IpColumnText>{ip.label}</Styles.IpColumnText>
                      </Styles.ItemLabel>
                      <Styles.ItemValue>
                        <Styles.IpColumnText>{ip.value}</Styles.IpColumnText>
                      </Styles.ItemValue>
                    </Styles.ItemLabelWrapper>
                  ))}
                </Styles.Item>
              );
            }
            return (
              <Styles.Item key="vps-info-ips">
                <Styles.ItemLabel>
                  {item.icon}
                  <Styles.IpColumn>
                    {item.ips.map(ip => (
                      <Styles.IpColumnText key={ip.label}>{ip.label}</Styles.IpColumnText>
                    ))}
                  </Styles.IpColumn>
                </Styles.ItemLabel>
                <Styles.ItemValue>
                  <Styles.IpColumn>
                    {item.ips.map(ip => (
                      <Styles.IpColumnText key={ip.value}>{ip.value}</Styles.IpColumnText>
                    ))}
                  </Styles.IpColumn>
                </Styles.ItemValue>
              </Styles.Item>
            );
          }
          return (
            <Styles.Item key={item.label || index}>
              <Styles.ItemLabel>
                {item.icon && item.icon}
                {item.label && item.label}
              </Styles.ItemLabel>
              <Styles.ItemValue>{item.value && item.value}</Styles.ItemValue>
            </Styles.Item>
          );
        })}
      </Styles.ListItems>
    </ContentSection>
  );
});

VPSInfo.displayName = 'VPSInfo';

export default VPSInfo;
